// Guided Clavian Boot setup on the venue's Setup page (spec/13 §13.1, F-BOOT-29): five steps that get a venue's PCs starting
// from its boot server without technical staff — the boot server paired, a PC seen starting from the network, the first
// Windows image, the games, the PCs on the floor. The group is suggested, never essential, and is not counted in "Setup · n
// left". Each step ticks by itself from what the venue has (`boot-setup.ts`): the lists are re-read when the window regains
// focus, after a dialog here closes, and on `boot.server.changed` / `boot.pc.changed` / `boot.job.progress` while the page is
// open. A stuck step says what is wrong in a sentence. A venue with Clavian Boot only gets this group first and it is not called
// a product; with both products it is "Clavian Boot".
//
// The network start test is a panel in its step, not a page of its own: "Start the test" records where the boot server's
// last network start stood, and waits; a later one is "We saw it", and nothing within 90 seconds says what usually
// causes that (a router answering network starts, or the boot server's network set to Public) with a picture of the fix.

import type { BootNetworkStartSeen, BootServer } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, ProgressBar } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { type Access } from '../api/queries';
import { problemDetail } from '../api/problems';
import { hardwareLine } from '../boot/hardware';
import { progressNow, progressStatus } from '../boot/format';
import { bootKeys, useBootFloor, useBootImages, useBootNewPcs, useBootServers, useCreatePairingCode, type BootScope } from '../boot/queries';
import { NewImageDialog } from '../boot/NewImageDialog';
import { useBootJobs, useBootLive } from '../boot/realtime';
import { Icon } from '../shell/nav-icons';
import { Notice, Section } from '../ui';
import {
  BOOT_SETUP_STEPS,
  type BootSetupStepId,
  bootSetupSteps,
  firstLive,
  lastNetworkStartOf,
  NETWORK_TEST_WAIT_MS,
  networkStartSince,
  readBootSkips,
  SKIPPABLE_BOOT_STEPS,
  writeBootSkips,
} from './boot-setup';

import './boot-setup.css';

const BOOT_VIEW = 'boot:view';
const BOOT_MANAGE = 'boot:manage';

/** The group is offered to a member who may read boot; a venue without the add-on answers 402 and the group stays out of the way. */
export function offersBootSetup(access: Access): boolean {
  return access.status === 'ready' && access.can(BOOT_VIEW);
}

/**
 * The Clavian Boot group. Nothing while the venue's boot server has not been read, or when the organisation does not have the
 * add-on (the read answers 402 and the group stays out of the way).
 */
export function BootSetupGroup({
  scope,
  venueSlug,
  access,
  bootOnly,
  title,
}: {
  readonly scope: BootScope;
  readonly venueSlug: string;
  readonly access: Access;
  /** A venue with Clavian Boot and no Clavian Stations: the group is not called a product. */
  readonly bootOnly: boolean;
  readonly title?: string;
}): ReactNode {
  const { t } = useTranslation('console');
  const queryClient = useQueryClient();
  const enabled = offersBootSetup(access);
  const canManage = access.status === 'ready' && access.can(BOOT_MANAGE);
  const servers = useBootServers(scope, enabled);
  const images = useBootImages(scope, enabled && servers.isSuccess);
  const floor = useBootFloor(scope, enabled && servers.isSuccess);
  const newPcs = useBootNewPcs(scope, enabled && servers.isSuccess);
  const jobs = useBootJobs(scope);
  useBootLive(scope, enabled && servers.isSuccess);
  const [serverDialog, setServerDialog] = useState(false);
  const [imageDialog, setImageDialog] = useState(false);
  const [gamesDialog, setGamesDialog] = useState(false);
  const [testing, setTesting] = useState(false);
  const [skips, setSkips] = useState<ReadonlySet<BootSetupStepId>>(() => readBootSkips(scope.venueId));

  // A step is ticked by something done elsewhere: read the boot lists again when the window regains focus.
  useEffect(() => {
    if (!enabled) return;
    const reread = () => void queryClient.invalidateQueries({ queryKey: bootKeys.all(scope.organizationId, scope.venueId) });
    window.addEventListener('focus', reread);
    return () => window.removeEventListener('focus', reread);
  }, [enabled, queryClient, scope.organizationId, scope.venueId]);

  // "Not seen for N minutes" counts from now: keep it fresh while the page is open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (!enabled || !servers.isSuccess) return null;
  const serverList = servers.data;
  const imageList = images.data ?? [];
  const facts = { servers: serverList, images: imageList, floorPcs: floor.data ?? [], newPcs: newPcs.data ?? [], now };
  const statuses = bootSetupSteps(facts);
  const online = serverList[0]?.status === 'online';
  const settled = statuses.filter((step) => step.done || skips.has(step.id)).length;
  const hasDefault = imageList.some((image) => image.kind === 'system' && image.defaultForVenue && image.archivedAt === null);
  const making = imageList.find((image) => image.kind === 'system' && image.versionInProgress != null);
  const reread = () => void queryClient.invalidateQueries({ queryKey: bootKeys.all(scope.organizationId, scope.venueId) });

  const skip = (id: BootSetupStepId, on: boolean) => {
    const next = new Set(skips);
    if (on) next.add(id);
    else next.delete(id);
    setSkips(next);
    writeBootSkips(scope.venueId, next);
  };

  const bootPage = (page: 'images' | 'games' | undefined) => ({ to: '/v/$venueSlug/boot' as const, params: { venueSlug }, search: (page === undefined ? {} : { page }) as never });
  const linkClass = (variant: 'primary' | 'secondary' | 'ghost') => `clav-button clav-button--${variant} clav-button--sm`;

  return (
    <Section
      title={title ?? t(bootOnly ? 'bootGuide.setup.group.titleOnly' : 'bootGuide.setup.group.titleBoth')}
      tip={t(bootOnly ? 'bootGuide.setup.group.hintOnly' : 'bootGuide.setup.group.hintBoth')}
      actions={<Badge tone="neutral">{t('bootGuide.setup.group.progress', { done: settled, total: BOOT_SETUP_STEPS.length })}</Badge>}
      body="rows"
    >
      <ol className="console-setup-steps" aria-label={t('bootGuide.setup.group.label')}>
        {statuses.map((step, index) => {
          const skipped = !step.done && skips.has(step.id);
          const status = step.done ? 'done' : skipped ? 'skipped' : 'todo';
          const base = `bootGuide.setup.${step.id}`;
          const name = t(`${base}.title`);
          const needsServer = !online && !step.done && (step.id === 'networkStart' || step.id === 'image' || step.id === 'games');
          const variant = step.done ? 'ghost' : 'secondary';
          let action: ReactNode = null;
          let extra: ReactNode = null;
          switch (step.id) {
            case 'server':
              action = canManage ? (
                <Button variant={variant} size="sm" label={`${t(`${base}.action`)}: ${name}`} onClick={() => setServerDialog(true)}>
                  {t(`${base}.action`)}
                </Button>
              ) : null;
              if (step.done) extra = <p className="console-subtle console-setup-step__line">{t(`${base}.doneLine`, { name: serverList[0]?.name ?? '' })}</p>;
              break;
            case 'networkStart':
              action = (
                <Button variant={variant} size="sm" disabled={needsServer} aria-expanded={testing} label={`${t(`${base}.action`)}: ${name}`} onClick={() => setTesting((open) => !open)}>
                  {t(`${base}.action`)}
                </Button>
              );
              if (step.done && !testing) extra = <p className="console-subtle console-setup-step__line">{t(`${base}.doneLine`)}</p>;
              break;
            case 'image': {
              const version = making?.versionInProgress ?? null;
              if (making !== undefined && version !== null && !step.done) {
                const now = progressNow(version.progress, jobs[version.id]);
                const status = progressStatus(version.state, now);
                extra = (
                  <div className="console-setup-step__progress">
                    <p className="console-subtle console-setup-step__line">
                      {status.percent === null
                        ? `${t('bootGuide.setup.image.inProgress', { name: making.name })} · ${t(`boot.images.status.${status.label}`)}`
                        : `${t('bootGuide.setup.image.inProgress', { name: making.name })} · ${t('bootGuide.setup.image.percent', { what: t(`boot.images.status.${status.label}`), percent: Math.round(status.percent) })}`}
                    </p>
                    {status.percent === null ? null : <ProgressBar value={status.percent / 100} label={t('bootGuide.setup.image.inProgress', { name: making.name })} size="sm" />}
                  </div>
                );
                action = canManage ? (
                  <Button variant="secondary" size="sm" label={`${t(`${base}.actionProgress`)}: ${name}`} onClick={() => setImageDialog(true)}>
                    {t(`${base}.actionProgress`)}
                  </Button>
                ) : null;
              } else if (step.done) {
                extra = <p className="console-subtle console-setup-step__line">{t(`${base}.doneLine`, { name: firstLive(imageList, 'system')?.name ?? '' })}</p>;
                action = (
                  <Link className={linkClass('ghost')} {...bootPage('images')} aria-label={`${t(`${base}.actionDone`)}: ${name}`}>
                    {t(`${base}.actionDone`)}
                  </Link>
                );
              } else {
                action = canManage ? (
                  <Button variant="secondary" size="sm" disabled={needsServer} label={`${t(`${base}.action`)}: ${name}`} onClick={() => setImageDialog(true)}>
                    {t(`${base}.action`)}
                  </Button>
                ) : null;
              }
              break;
            }
            case 'games':
              if (step.done) extra = <p className="console-subtle console-setup-step__line">{t(`${base}.doneLine`, { name: firstLive(imageList, 'game')?.name ?? '' })}</p>;
              // The folder wizard right here, for a member who manages Clavian Boot; else the Games page, which says what the role may do.
              action = needsServer ? null : canManage && !step.done ? (
                <Button variant="secondary" size="sm" label={`${t(`${base}.action`)}: ${name}`} onClick={() => setGamesDialog(true)}>
                  {t(`${base}.action`)}
                </Button>
              ) : (
                <Link className={linkClass(variant)} {...bootPage('games')} aria-label={`${t(step.done ? `${base}.actionDone` : `${base}.action`)}: ${name}`}>
                  {t(step.done ? `${base}.actionDone` : `${base}.action`)}
                </Link>
              );
              break;
            case 'pcs':
              if (step.done) extra = <p className="console-subtle console-setup-step__line">{t(`${base}.doneLine`, { count: facts.floorPcs.length })}</p>;
              action = (
                <Link className={linkClass(variant)} to="/v/$venueSlug" params={{ venueSlug }} aria-label={`${t(`${base}.action`)}: ${name}`}>
                  {t(`${base}.action`)}
                </Link>
              );
              break;
          }
          const stuck = step.stuck === null || step.done ? null : t(`bootGuide.setup.${step.stuck.key}`, step.stuck.values);
          const hint = needsServer ? t(`bootGuide.setup.${step.id}.needServer`) : null;
          const noRights = !step.done && !canManage && (step.id === 'server' || step.id === 'image') ? t('bootGuide.setup.noRights') : null;
          const skippable = SKIPPABLE_BOOT_STEPS.includes(step.id);
          return (
            <li key={step.id} className="console-setup-step" data-status={status} data-kind="suggested" data-boot-step={step.id}>
              <span className="console-setup-step__mark" aria-hidden="true">
                {step.done ? <Icon name="check" /> : skipped ? <Icon name="minus" /> : index + 1}
              </span>
              <div className="console-setup-step__text">
                <h3 className="console-setup-step__title">{name}</h3>
                <p className="console-setup-step__why">{t(`${base}.why`)}</p>
                {stuck === null ? null : (
                  <p className="console-setup-step__stuck" role="status">
                    <Icon name="alert" />
                    <span>{stuck}</span>
                  </p>
                )}
                {hint === null && noRights === null ? null : <p className="console-subtle console-setup-step__line">{hint ?? noRights}</p>}
                {skipped ? <p className="console-subtle console-setup-step__skipped">{t('bootGuide.setup.skippedHere')}</p> : null}
                {extra}
              </div>
              <div className="console-setup-step__state">
                <Badge tone={step.done ? 'ok' : 'neutral'} dot>
                  {t(`bootGuide.setup.status.${status}`)}
                </Badge>
              </div>
              <div className="console-setup-step__actions">
                {skipped ? null : action}
                {skippable && !step.done && !skipped ? (
                  <Button variant="ghost" size="sm" label={t('bootGuide.setup.skipNamed', { step: name })} onClick={() => skip(step.id, true)}>
                    {t('bootGuide.setup.skip')}
                  </Button>
                ) : null}
                {skipped ? (
                  <Button variant="ghost" size="sm" label={t('bootGuide.setup.unskipNamed', { step: name })} onClick={() => skip(step.id, false)}>
                    {t('bootGuide.setup.unskip')}
                  </Button>
                ) : null}
              </div>
              {step.id === 'networkStart' && testing && online ? (
                <div className="console-setup-step__panel">
                  <NetworkTest servers={serverList} newPcs={newPcs.data ?? []} onRefresh={reread} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      <ServerDialog scope={scope} open={serverDialog} onClose={() => { setServerDialog(false); reread(); }} />
      {canManage && imageDialog ? (
        <NewImageDialog
          scope={scope}
          open
          initialSource="capture"
          kind="system"
          hasDefault={hasDefault}
          onClose={() => {
            setImageDialog(false);
            reread();
          }}
          resume={making?.versionInProgress == null ? undefined : { image: making, version: making.versionInProgress }}
        />
      ) : null}
      {canManage && gamesDialog ? (
        <NewImageDialog
          scope={scope}
          open
          initialSource="folder"
          kind="game"
          hasDefault={hasDefault}
          onClose={() => {
            setGamesDialog(false);
            reread();
          }}
        />
      ) : null}
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// The network start test
// ---------------------------------------------------------------------------------------------------------------

type TestPhase = 'idle' | 'waiting' | 'nothing';

/**
 * "Switch on one PC and start it from the network." Idle: what to do and Start the test. Waiting: the page records where the
 * boot server's last network start stood and watches for a later one, reading again every few seconds on top of the live
 * push. Seen: "We saw it" with the PC's network address (and its hardware once it is a New PC). After 90 seconds with
 * nothing, what usually causes that, with a picture, and Try again — a start that arrives later still counts.
 */
export function NetworkTest({
  servers,
  newPcs,
  onRefresh,
  wait = NETWORK_TEST_WAIT_MS,
}: {
  readonly servers: readonly BootServer[];
  readonly newPcs: readonly { readonly mac: string; readonly gpuFamily: string | null; readonly cpu: string | null; readonly nicPciIds: readonly string[] }[];
  readonly onRefresh: () => void;
  readonly wait?: number;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const [phase, setPhase] = useState<TestPhase>('idle');
  // Where the boot server's last network start stood when the test began: only a later one is the test's PC.
  const [baseline, setBaseline] = useState<string | null>(null);
  // The parent draws often; the poll keeps one timer and calls the newest `onRefresh`.
  const refresh = useRef(onRefresh);
  useEffect(() => {
    refresh.current = onRefresh;
  });

  // Seen is read from the boot server's answer, not stored: a start that arrives late still counts.
  const seen: BootNetworkStartSeen | null = phase === 'waiting' || phase === 'nothing' ? networkStartSince(servers, baseline) : null;

  useEffect(() => {
    if (phase !== 'waiting') return;
    const give = window.setTimeout(() => setPhase('nothing'), wait);
    return () => window.clearTimeout(give);
  }, [phase, wait]);

  useEffect(() => {
    if (phase !== 'waiting' && phase !== 'nothing') return;
    const poll = window.setInterval(() => refresh.current(), 5_000);
    return () => window.clearInterval(poll);
  }, [phase]);

  const start = () => {
    setBaseline(lastNetworkStartOf(servers)?.at ?? null);
    setPhase('waiting');
  };

  if (seen !== null) {
    const known = newPcs.find((pc) => pc.mac.toLowerCase() === seen.mac.toLowerCase());
    const hardware = known === undefined ? '' : hardwareLine(known, t);
    return (
      <div className="console-boot-test">
        <Notice tone="ok" role="status" title={t('bootGuide.setup.networkStart.seenTitle')}>
          <p>{t('bootGuide.setup.networkStart.seen', { mac: seen.mac })}</p>
          {hardware === '' ? null : <p>{t('bootGuide.setup.networkStart.seenHardware', { hardware })}</p>}
          <p>{t('bootGuide.setup.networkStart.seenWhen', { when: format.relative(seen.at) })}</p>
        </Notice>
      </div>
    );
  }

  if (phase === 'idle') {
    return (
      <div className="console-boot-test">
        <p>{t('bootGuide.setup.networkStart.intro')}</p>
        <ol className="console-boot-test__steps">
          <li>{t('bootGuide.setup.networkStart.step1')}</li>
          <li>{t('bootGuide.setup.networkStart.step2')}</li>
          <li>{t('bootGuide.setup.networkStart.step3')}</li>
        </ol>
        <div>
          <Button variant="primary" size="sm" onClick={start}>
            {t('bootGuide.setup.networkStart.start')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="console-boot-test">
      {phase === 'waiting' ? (
        <>
          <p className="console-boot-test__waiting" role="status">
            <Icon name="refresh" />
            <span>{t('bootGuide.setup.networkStart.waiting')}</span>
          </p>
          <ol className="console-boot-test__steps">
            <li>{t('bootGuide.setup.networkStart.step1')}</li>
            <li>{t('bootGuide.setup.networkStart.step2')}</li>
            <li>{t('bootGuide.setup.networkStart.step3')}</li>
          </ol>
          <div>
            <Button variant="ghost" size="sm" onClick={() => setPhase('idle')}>
              {t('bootGuide.setup.networkStart.stop')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <Notice tone="warn" role="status" title={t('bootGuide.setup.networkStart.nothingTitle')}>
            <p>{t('bootGuide.setup.networkStart.nothing')}</p>
          </Notice>
          <div className="console-boot-test__fixes">
            <section aria-labelledby="console-boot-test-switch">
              <h4 id="console-boot-test-switch" className="console-boot-test__heading">
                {t('bootGuide.setup.networkStart.switchTitle')}
              </h4>
              <p>{t('bootGuide.setup.networkStart.switchBody')}</p>
              <SwitchDiagram />
            </section>
            <section aria-labelledby="console-boot-test-public">
              <h4 id="console-boot-test-public" className="console-boot-test__heading">
                {t('bootGuide.setup.networkStart.publicTitle')}
              </h4>
              <ol className="console-boot-test__steps">
                <li>{t('bootGuide.setup.networkStart.public1')}</li>
                <li>{t('bootGuide.setup.networkStart.public2')}</li>
                <li>{t('bootGuide.setup.networkStart.public3')}</li>
                <li>{t('bootGuide.setup.networkStart.public4')}</li>
              </ol>
            </section>
          </div>
          <div>
            <Button variant="primary" size="sm" onClick={start}>
              {t('bootGuide.setup.networkStart.tryAgain')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** Router, then a switch, then the boot server and the PCs on the switch: the picture of "put a switch between them". */
function SwitchDiagram(): ReactNode {
  const { t } = useTranslation('console');
  return (
    <svg className="console-boot-diagram" viewBox="0 0 360 176" role="img" aria-labelledby="console-boot-diagram-title console-boot-diagram-desc">
      <title id="console-boot-diagram-title">{t('bootGuide.setup.networkStart.diagramTitle')}</title>
      <desc id="console-boot-diagram-desc">{t('bootGuide.setup.networkStart.diagramDesc')}</desc>
      <path className="console-boot-diagram__line" d="M180 40V68M180 100L90 132M180 100L270 132" />
      <rect className="console-boot-diagram__box" x="130" y="8" width="100" height="32" rx="6" />
      <text className="console-boot-diagram__text" x="180" y="29" textAnchor="middle">
        {t('bootGuide.setup.networkStart.diagramRouter')}
      </text>
      <rect className="console-boot-diagram__box console-boot-diagram__box--key" x="130" y="68" width="100" height="32" rx="6" />
      <text className="console-boot-diagram__text" x="180" y="89" textAnchor="middle">
        {t('bootGuide.setup.networkStart.diagramSwitch')}
      </text>
      <rect className="console-boot-diagram__box" x="20" y="132" width="140" height="32" rx="6" />
      <text className="console-boot-diagram__text" x="90" y="153" textAnchor="middle">
        {t('bootGuide.setup.networkStart.diagramServer')}
      </text>
      <rect className="console-boot-diagram__box" x="200" y="132" width="140" height="32" rx="6" />
      <text className="console-boot-diagram__text" x="270" y="153" textAnchor="middle">
        {t('bootGuide.setup.networkStart.diagramPcs')}
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Set up the boot server
// ---------------------------------------------------------------------------------------------------------------

/** What to do on the boot server's computer, and the pairing code to type in. Closing it re-reads the step. */
function ServerDialog({ scope, open, onClose }: { readonly scope: BootScope; readonly open: boolean; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const create = useCreatePairingCode(scope);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const code = create.data;

  function close(): void {
    create.reset();
    setFailure(undefined);
    onClose();
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    create.mutate({}, { onError: (caught) => setFailure(problemDetail(caught) ?? t('bootGuide.setup.serverDialog.failed')) });
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())} size="md" title={t('bootGuide.setup.serverDialog.title')} description={t('bootGuide.setup.serverDialog.intro')}>
      <form className="console-form" noValidate aria-label={t('bootGuide.setup.serverDialog.title')} onSubmit={submit}>
        <ol className="console-boot-test__steps">
          <li>{t('bootGuide.setup.serverDialog.step1')}</li>
          <li>{t('bootGuide.setup.serverDialog.step2')}</li>
          <li>{t('bootGuide.setup.serverDialog.step3')}</li>
        </ol>
        {failure === undefined ? null : (
          <Notice tone="danger" role="alert">
            {failure}
          </Notice>
        )}
        {code === undefined ? null : (
          <div className="console-boot-code" role="status">
            <span className="console-subtle">{t('bootGuide.setup.serverDialog.codeIntro')}</span>
            <p className="console-boot-code__value" aria-label={t('bootGuide.setup.serverDialog.codeLabel')}>
              {code.code}
            </p>
            <span className="console-subtle">{t('bootGuide.setup.serverDialog.expires', { time: format.time(code.expiresAt, 'short') })}</span>
            <span className="console-subtle">{t('bootGuide.setup.serverDialog.waiting')}</span>
          </div>
        )}
        <div className="console-form__actions">
          {code === undefined ? (
            <>
              <Button variant="ghost" onClick={close}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" busy={create.isPending}>
                {t('bootGuide.setup.serverDialog.get')}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={close}>
              {t('bootGuide.setup.serverDialog.done')}
            </Button>
          )}
        </div>
      </form>
    </Dialog>
  );
}
